import Phaser from 'phaser';
import { RESOURCES } from '../scenes/GameScene';

// Color palette matching Pryor Mountain backgrounds
const COLORS = {
  bone: 0xD2CFBF,        // Light tan/bone
  cornflower: 0x6B94E4,  // Sky blue
  oliveWood: 0x8B5A2B,   // Brown wood
  deepForest: 0x163F29,  // Dark green
  cream: 0xE8E4D8,       // Soft cream
  textLight: '#E8E4D8',
  success: 0x4A7C59,     // Muted green
  danger: 0xA65D57,      // Muted red
  water: 0x5B8FA8,       // Muted blue
  panelBg: 0x1A2F1A,     // Dark forest panel
};

export class ResourceDisplay extends Phaser.GameObjects.Container {
  private populationText!: Phaser.GameObjects.Text;
  private forageBar!: Phaser.GameObjects.Rectangle;
  private forageText!: Phaser.GameObjects.Text;
  private waterBar!: Phaser.GameObjects.Rectangle;
  private waterText!: Phaser.GameObjects.Text;
  private healthBar!: Phaser.GameObjects.Rectangle;
  private healthText!: Phaser.GameObjects.Text;
  private fundsText!: Phaser.GameObjects.Text;
  private yearText!: Phaser.GameObjects.Text;
  private amlText!: Phaser.GameObjects.Text;
  private reputationText!: Phaser.GameObjects.Text;
  private blmText!: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    
    // Expanded panel for new stats
    const panelWidth = 185;
    const panelHeight = 200;
    const panel = scene.add.rectangle(panelWidth/2, panelHeight/2, panelWidth, panelHeight, COLORS.panelBg, 0.92)
      .setStrokeStyle(1, COLORS.bone);
    this.add(panel);
    
    // Year display - compact with stroke
    this.yearText = scene.add.text(panelWidth/2, 12, `${RESOURCES.year}`, {
      fontFamily: 'Lora, Georgia, serif',
      fontSize: '16px',
      fontStyle: 'bold',
      color: '#FFF8DC',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5);
    this.add(this.yearText);
    
    // Population row
    const popIcon = scene.add.text(8, 28, '🐴', { fontSize: '14px' });
    this.populationText = scene.add.text(28, 30, `${RESOURCES.population}`, {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '14px',
      fontStyle: 'bold',
      color: '#FFFFFF',
      stroke: '#000000',
      strokeThickness: 2
    });
    this.amlText = scene.add.text(62, 32, `/ 120`, {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '10px',
      color: '#999999'
    });
    this.add([popIcon, this.populationText, this.amlText]);
    
    // Resource bars - compact
    const barStartX = 28;
    const barWidth = 100;
    const barHeight = 10;
    
    // Forage bar
    const forageIcon = scene.add.text(8, 46, '🌿', { fontSize: '12px' });
    const forageBg = scene.add.rectangle(barStartX + barWidth / 2, 53, barWidth, barHeight, 0x333333);
    this.forageBar = scene.add.rectangle(barStartX, 53, 0, barHeight - 2, 0x4a7c23).setOrigin(0, 0.5);
    this.forageText = scene.add.text(barStartX + barWidth + 4, 48, `${RESOURCES.forage}%`, {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '10px',
      color: '#90EE90',
      stroke: '#000000',
      strokeThickness: 1
    });
    this.add([forageIcon, forageBg, this.forageBar, this.forageText]);
    
    // Water bar
    const waterIcon = scene.add.text(8, 64, '💧', { fontSize: '12px' });
    const waterBg = scene.add.rectangle(barStartX + barWidth / 2, 71, barWidth, barHeight, 0x333333);
    this.waterBar = scene.add.rectangle(barStartX, 71, 0, barHeight - 2, 0x4a90b8).setOrigin(0, 0.5);
    this.waterText = scene.add.text(barStartX + barWidth + 4, 66, `${RESOURCES.water}%`, {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '10px',
      color: '#87CEEB',
      stroke: '#000000',
      strokeThickness: 1
    });
    this.add([waterIcon, waterBg, this.waterBar, this.waterText]);
    
    // Herd Health bar (replaces diversity in main view)
    const healthIcon = scene.add.text(8, 82, '❤️', { fontSize: '12px' });
    const healthBg = scene.add.rectangle(barStartX + barWidth / 2, 89, barWidth, barHeight, 0x333333);
    this.healthBar = scene.add.rectangle(barStartX, 89, 0, barHeight - 2, 0xE07070).setOrigin(0, 0.5);
    this.healthText = scene.add.text(barStartX + barWidth + 4, 84, `${RESOURCES.herdHealth}%`, {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '10px',
      color: '#FF9999',
      stroke: '#000000',
      strokeThickness: 1
    });
    this.add([healthIcon, healthBg, this.healthBar, this.healthText]);
    
    // Funds row
    const fundsIcon = scene.add.text(8, 102, '💰', { fontSize: '12px' });
    this.fundsText = scene.add.text(28, 104, `$${RESOURCES.funds.toLocaleString()}`, {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#FFD700',
      stroke: '#000000',
      strokeThickness: 2
    });
    this.add([fundsIcon, this.fundsText]);
    
    // Divider line
    const divider = scene.add.rectangle(panelWidth/2, 122, panelWidth - 20, 1, COLORS.bone, 0.3);
    this.add(divider);
    
    // NEW: Reputation indicator
    const repIcon = scene.add.text(8, 130, '⭐', { fontSize: '11px' });
    const repLabel = scene.add.text(24, 132, 'Rep:', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '10px',
      color: '#AAAAAA'
    });
    this.reputationText = scene.add.text(50, 132, `${RESOURCES.reputation}`, {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '10px',
      fontStyle: 'bold',
      color: '#FFD700',
      stroke: '#000000',
      strokeThickness: 1
    });
    this.add([repIcon, repLabel, this.reputationText]);
    
    // NEW: BLM Relations indicator
    const blmIcon = scene.add.text(90, 130, '🏛️', { fontSize: '11px' });
    const blmLabel = scene.add.text(106, 132, 'BLM:', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '10px',
      color: '#AAAAAA'
    });
    this.blmText = scene.add.text(132, 132, `${RESOURCES.blmRelations}`, {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '10px',
      fontStyle: 'bold',
      color: '#87CEEB',
      stroke: '#000000',
      strokeThickness: 1
    });
    this.add([blmIcon, blmLabel, this.blmText]);
    
    // Diversity small indicator
    const divIcon = scene.add.text(8, 148, '🧬', { fontSize: '11px' });
    const divLabel = scene.add.text(24, 150, 'Diversity:', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '10px',
      color: '#AAAAAA'
    });
    const divText = scene.add.text(75, 150, `${RESOURCES.diversity}%`, {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '10px',
      color: '#DDA0DD',
      stroke: '#000000',
      strokeThickness: 1
    });
    this.add([divIcon, divLabel, divText]);
    
    scene.add.existing(this);
    this.setDepth(50);
    
    // Initial update
    this.update();
  }

  update(): void {
    const barWidth = 100; // Match constructor
    
    // Update year
    this.yearText.setText(`${RESOURCES.year}`);
    
    // Update population with AML context
    this.populationText.setText(`${RESOURCES.population}`);
    
    // Color code population based on AML
    if (RESOURCES.population > 180) {
      this.populationText.setColor('#FF6347'); // Danger - way over
    } else if (RESOURCES.population > 120) {
      this.populationText.setColor('#FFA500'); // Warning - over AML
    } else if (RESOURCES.population >= 90 && RESOURCES.population <= 120) {
      this.populationText.setColor('#90EE90'); // Perfect - in AML range
    } else if (RESOURCES.population < 50) {
      this.populationText.setColor('#FF6347'); // Danger - too low
    } else {
      this.populationText.setColor('#FFFFFF'); // Normal
    }
    
    // Update forage bar
    const forageWidth = Math.max(0, (RESOURCES.forage / 100) * barWidth);
    this.forageBar.width = forageWidth;
    this.forageText.setText(`${Math.round(RESOURCES.forage)}%`);
    
    if (RESOURCES.forage < 25) {
      this.forageBar.setFillStyle(0xFF6347);
      this.forageText.setColor('#FF6347');
    } else if (RESOURCES.forage < 50) {
      this.forageBar.setFillStyle(0xFFA500);
      this.forageText.setColor('#FFA500');
    } else {
      this.forageBar.setFillStyle(0x4a7c23);
      this.forageText.setColor('#90EE90');
    }
    
    // Update water bar
    const waterWidth = Math.max(0, (RESOURCES.water / 100) * barWidth);
    this.waterBar.width = waterWidth;
    this.waterText.setText(`${Math.round(RESOURCES.water)}%`);
    
    if (RESOURCES.water < 25) {
      this.waterBar.setFillStyle(0xFF6347);
      this.waterText.setColor('#FF6347');
    } else if (RESOURCES.water < 50) {
      this.waterBar.setFillStyle(0xFFA500);
      this.waterText.setColor('#FFA500');
    } else {
      this.waterBar.setFillStyle(0x4a90b8);
      this.waterText.setColor('#87CEEB');
    }
    
    // Update herd health bar
    const healthWidth = Math.max(0, (RESOURCES.herdHealth / 100) * barWidth);
    this.healthBar.width = healthWidth;
    this.healthText.setText(`${Math.round(RESOURCES.herdHealth)}%`);
    
    if (RESOURCES.herdHealth < 30) {
      this.healthBar.setFillStyle(0xFF6347);
      this.healthText.setColor('#FF6347');
    } else if (RESOURCES.herdHealth < 50) {
      this.healthBar.setFillStyle(0xFFA500);
      this.healthText.setColor('#FFA500');
    } else if (RESOURCES.herdHealth >= 80) {
      this.healthBar.setFillStyle(0x90EE90);
      this.healthText.setColor('#90EE90');
    } else {
      this.healthBar.setFillStyle(0xE07070);
      this.healthText.setColor('#FF9999');
    }
    
    // Update funds
    this.fundsText.setText(`$${RESOURCES.funds.toLocaleString()}`);
    
    if (RESOURCES.funds < 2000) {
      this.fundsText.setColor('#FF6347');
    } else if (RESOURCES.funds < 5000) {
      this.fundsText.setColor('#FFA500');
    } else {
      this.fundsText.setColor('#FFD700');
    }
    
    // Update reputation
    this.reputationText.setText(`${RESOURCES.reputation}`);
    if (RESOURCES.reputation < 30) {
      this.reputationText.setColor('#FF6347');
    } else if (RESOURCES.reputation >= 70) {
      this.reputationText.setColor('#90EE90');
    } else {
      this.reputationText.setColor('#FFD700');
    }
    
    // Update BLM relations
    this.blmText.setText(`${RESOURCES.blmRelations}`);
    if (RESOURCES.blmRelations < 30) {
      this.blmText.setColor('#FF6347');
    } else if (RESOURCES.blmRelations >= 70) {
      this.blmText.setColor('#90EE90');
    } else {
      this.blmText.setColor('#87CEEB');
    }
  }
}
